import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { nacmeLogo } from './assets/images';

const PromoVideo = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(null);
  const [showLoginWarning, setShowLoginWarning] = useState(false);
  const [showUnmutePrompt, setShowUnmutePrompt] = useState(false);
  const [player, setPlayer] = useState(null);

  useEffect(() => {
    // Check if there's a token in the URL
    const urlToken = searchParams.get('token');
    if (urlToken) {
      setToken(urlToken);
    }
  }, [searchParams]);

  const handleDashboardLoginClick = () => {
    if (token) {
      // Show warning modal if they have a token (need to complete application first)
      setShowLoginWarning(true);
    } else {
      // Allow login if no token
      navigate('/student/login');
    }
  };

  const handleUnmute = async () => {
    if (player) {
      try {
        await player.setMuted(false);
        await player.setVolume(1);
        setShowUnmutePrompt(false);
      } catch (error) {
        console.log('Could not unmute video:', error);
      }
    }
  };

  useEffect(() => {
    // Dynamically load the Vimeo player API script
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);

    // Initialize Vimeo player once script loads
    script.onload = () => {
      const iframe = document.querySelector('#promo-video-iframe');
      if (iframe && window.Vimeo) {
        const vimeoPlayer = new window.Vimeo.Player(iframe);
        setPlayer(vimeoPlayer);

        // Check if video is muted after it starts playing
        vimeoPlayer.on('play', async () => {
          try {
            const muted = await vimeoPlayer.getMuted();
            if (muted) {
              setShowUnmutePrompt(true);
            }
          } catch (error) {
            console.log('Could not check mute status:', error);
          }
        });

        // Try to unmute on load (may be blocked by browser)
        vimeoPlayer.ready().then(async () => {
          try {
            await vimeoPlayer.setMuted(false);
            await vimeoPlayer.setVolume(1);
          } catch (error) {
            // Browser blocked unmute - will show prompt when video plays
            console.log('Autoplay unmute blocked by browser');
          }
        });
      }
    };

    return () => {
      // Cleanup script when component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 animate-fade-in overflow-x-hidden">
      {/* Sticky Header - Same as Landing Page */}
      <header className="sticky top-0 z-50 bg-white/40 backdrop-blur-md border-b border-gray-200/50 shadow-sm w-full">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center gap-2 sm:gap-4 md:gap-6 flex-wrap sm:flex-nowrap">
          <img
            src={nacmeLogo}
            alt="NACME Logo"
            className="h-8 sm:h-10 w-auto drop-shadow-sm cursor-pointer flex-shrink-0"
            onClick={() => navigate('/')}
          />
          <button
            onClick={() => navigate('/')}
            className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent hover:from-orange-700 hover:to-amber-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-orange-50/50"
          >
            Home
          </button>
          <button
            onClick={() => navigate('/demo' + (token ? `?token=${token}` : ''))}
            className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent hover:from-emerald-700 hover:to-teal-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-emerald-50/50"
          >
            Demo
          </button>
          <div className="relative group hidden sm:block">
            <button className="text-xs sm:text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors px-2 sm:px-4 py-2 rounded-lg hover:bg-blue-50/50">
              About
            </button>
            {/* Tooltip on hover */}
            <div className="absolute left-0 top-full mt-2 w-[90vw] sm:w-[40rem] md:w-[50rem] lg:w-[60rem] max-w-4xl p-4 sm:p-5 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
              <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                NACME offers our recruiting partners access to high-achieving scholars and alumni for roles in engineering and computer science. Uniquely situated at the nexus of academia and industry, NACME is your pipeline to entry level, mid-career, and executive talent. Dedicated to fostering the next generation of engineers and computer scientists, we identify high-potential students at leading institutions and cultivate their academic and professional success with scholarships, programs, and career access.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 ml-auto">
            <button
              onClick={() => navigate(token ? `/update-application?token=${token}` : '/student/login')}
              className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent hover:from-blue-700 hover:to-indigo-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-blue-50/50 whitespace-nowrap"
            >
              {token ? 'Complete Your Application' : 'Student Login'}
            </button>
            <button
              onClick={handleDashboardLoginClick}
              className="text-xs sm:text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent hover:from-purple-700 hover:to-pink-700 transition-all px-2 sm:px-4 py-2 rounded-lg hover:bg-purple-50/50 whitespace-nowrap"
            >
              Dashboard Login
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 md:py-16">
        {/* Hero Section */}
        <div className="text-center mb-6 sm:mb-10 md:mb-16 mt-2">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 leading-tight pb-2 px-2">
            NACME Searchlight
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto font-light mb-2 px-4">
            Discover Your Next Engineering or CS Internship Today
          </p>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto font-light px-4">
            Watch how NACME Searchlight connects talented students with leading companies
          </p>
        </div>

        {/* Video Container */}
        <div className="w-full max-w-5xl mx-auto mb-8 sm:mb-12">
          <div className="group relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-2xl sm:rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-300 p-3 sm:p-6 md:p-8 border border-blue-200 overflow-hidden">
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-600/5 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            {/* Video Wrapper - Responsive 16:9 aspect ratio */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl">
              <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                <iframe
                  id="promo-video-iframe"
                  src="https://player.vimeo.com/video/1134208988?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1&amp;muted=0"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  title="NACME Searchlight - Find engineering and CS internships today!"
                />

                {/* Unmute Prompt Overlay */}
                {showUnmutePrompt && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                    <button
                      onClick={handleUnmute}
                      className="group relative px-8 py-4 bg-white text-blue-600 font-bold text-lg rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105 animate-pulse"
                    >
                      <div className="flex items-center gap-3">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                        </svg>
                        <span>Click to Enable Sound</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action Section */}
        <div className="w-full max-w-4xl mx-auto px-2">
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-10 md:p-12 text-center">
            {token ? (
              // Show application completion CTA if token is present
              <>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                  Ready to Complete Your Application?
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-blue-50 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
                  Continue to set your password and update your profile to connect with top companies
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-2">
                  <button
                    onClick={() => navigate(`/update-application?token=${token}`)}
                    className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-white text-blue-600 font-bold text-lg rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 hover:scale-105"
                  >
                    Complete Your Application →
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-blue-100 mt-4 px-2">
                  Your secure access link is ready. Click above to continue.
                </p>
              </>
            ) : (
              // Show general CTAs if no token
              <>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
                  Ready to Get Started?
                </h2>
                <p className="text-base sm:text-lg md:text-xl text-blue-50 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
                  Join thousands of students connecting with top companies for internships and careers
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-2">
                  <button
                    onClick={() => navigate('/student/login')}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Student Login
                  </button>
                  <button
                    onClick={() => navigate('/recruiter/login')}
                    className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-white/20 backdrop-blur-sm text-white font-bold rounded-xl hover:bg-white/30 transition-all duration-300 border-2 border-white/50 hover:border-white shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Recruiter Login
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Additional Info Section */}
        <div className="mt-10 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 w-full max-w-5xl mx-auto px-2">
          {/* Feature 1 */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Top Companies</h3>
            <p className="text-sm text-gray-600">Connect with leading engineering and tech companies</p>
          </div>

          {/* Feature 2 */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-indigo-400 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Quality Opportunities</h3>
            <p className="text-sm text-gray-600">Curated internships and full-time positions</p>
          </div>

          {/* Feature 3 */}
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-200 text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-pink-600 rounded-xl flex items-center justify-center shadow-md">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Quick Matches</h3>
            <p className="text-sm text-gray-600">Fast, intelligent matching with your ideal positions</p>
          </div>
        </div>
      </div>

      {/* Login Warning Modal */}
      {showLoginWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 transform transition-all animate-scale-in">
            {/* Warning Icon */}
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
              Complete Your Application First!
            </h3>

            {/* Message */}
            <p className="text-gray-600 text-center mb-6 leading-relaxed">
              You cannot login to your dashboard until you've completed your application. Please click "Complete Your Application" to set your password and update your profile first.
            </p>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowLoginWarning(false);
                  navigate(`/update-application?token=${token}`);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Complete Your Application Now
              </button>
              <button
                onClick={() => setShowLoginWarning(false)}
                className="w-full px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoVideo;
